import React from 'react';
import { Helmet } from 'react-helmet-async';
interface MetaTagsProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  schema?: object | object[];
  language?: string;
  alternateUrls?: Array<{
    lang: string;
    url: string;
  }>;
}
export function MetaTags({
  title = 'ToxicDetector – Detect Toxic Messages Instantly',
  description = 'Analyze messages for toxicity, gaslighting, manipulation and emotional abuse using AI. 100% private and encrypted.',
  keywords = 'AI toxic relationship detector, detect gaslighting, manipulation detector, toxic text analyzer, gaslighting in text messages, emotional abuse detector, AI relationship analysis, toxic ex checker',
  canonical = 'https://toxicdetector.com/',
  ogTitle,
  ogDescription,
  ogImage = 'https://cdn.magicpatterns.com/uploads/wonwX8zVL7YiLQwtTT8xq8/file_00000000e69c7243bed09cbc104eb039.png',
  ogType = 'website',
  schema,
  language = 'en',
  alternateUrls = []
}: MetaTagsProps) {
  // Default JSON-LD Schema Markup for Rich Snippets
  const defaultSchemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'ToxicDetector',
    operatingSystem: 'Web, iOS, Android',
    applicationCategory: 'HealthApplication',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '7240'
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free Scan Trial'
    },
    description: 'ToxicDetector is an advanced AI toxic relationship detector designed to uncover hidden manipulation, gaslighting, and emotional abuse patterns in digital conversations.',
    url: 'https://toxicdetector.com/'
  };
  // Use custom schema if provided, otherwise use default
  const schemaMarkup = schema || defaultSchemaMarkup;
  // Default hreflang for homepage if none provided
  const defaultAlternateUrls = [{
    lang: 'en',
    url: 'https://toxicdetector.com/'
  }, {
    lang: 'pt',
    url: 'https://toxicdetector.com/pt'
  }, {
    lang: 'es',
    url: 'https://toxicdetector.com/es'
  }, {
    lang: 'ja',
    url: 'https://toxicdetector.com/ja'
  }, {
    lang: 'ko',
    url: 'https://toxicdetector.com/ko'
  }, {
    lang: 'de',
    url: 'https://toxicdetector.com/de'
  }, {
    lang: 'fr',
    url: 'https://toxicdetector.com/fr'
  }, {
    lang: 'hi',
    url: 'https://toxicdetector.com/hi'
  }, {
    lang: 'id',
    url: 'https://toxicdetector.com/id'
  }, {
    lang: 'x-default',
    url: 'https://toxicdetector.com/'
  }];
  const hreflangUrls = alternateUrls.length > 0 ? alternateUrls : defaultAlternateUrls;
  return <Helmet data-id="element-1694">
      {/* Primary Meta Tags */}
      <title data-id="element-1695">{title}</title>
      <meta name="title" content={title} data-id="element-1696" />
      <meta name="description" content={description} data-id="element-1697" />
      <meta name="keywords" content={keywords} data-id="element-1698" />
      <meta name="robots" content="index, follow" data-id="element-1699" />
      <meta name="language" content={language} data-id="element-1700" />
      <meta name="author" content="Chukwuka Paulgreen Agodi" data-id="element-1701" />
      <link rel="canonical" href={canonical} data-id="element-1702" />

      {/* Hreflang Tags for International SEO */}
      {hreflangUrls.map(alternate => <link key={alternate.lang} rel="alternate" hrefLang={alternate.lang} href={alternate.url} data-id="element-1703" />)}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} data-id="element-1704" />
      <meta property="og:url" content={canonical} data-id="element-1705" />
      <meta property="og:title" content={ogTitle || title} data-id="element-1706" />
      <meta property="og:description" content={ogDescription || description} data-id="element-1707" />
      <meta property="og:image" content={ogImage} data-id="element-1708" />
      <meta property="og:site_name" content="ToxicDetector" data-id="element-1709" />
      <meta property="og:locale" content={language === 'en' ? 'en_US' : language} data-id="element-1710" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" data-id="element-1711" />
      <meta name="twitter:url" content={canonical} data-id="element-1712" />
      <meta name="twitter:title" content={ogTitle || title} data-id="element-1713" />
      <meta name="twitter:description" content={ogDescription || description} data-id="element-1714" />
      <meta name="twitter:image" content={ogImage} data-id="element-1715" />

      {/* JSON-LD Schema Markup */}
      <script type="application/ld+json" data-id="element-1716">{JSON.stringify(schemaMarkup)}</script>

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" data-id="element-1717" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" data-id="element-1718" />
      <meta name="theme-color" content="#9333ea" data-id="element-1719" />
    </Helmet>;
}